import { Alert, Box, IconButton, Menu, MenuItem, Typography } from '@mui/material'
import {
  AlertCircleIcon,
  ArrowNarrowRightIcon,
  DotsVerticalIcon,
  FilterLinesIcon,
} from '@untitled-theme/icons-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { useRetrofitFinancing } from '@/shared/api'
import { BedarfVerbrauchLabel, BedarfVerbrauchType } from '@/shared/components/BedarfVerbrauchLabel'
import Button from '@/shared/components/Button'
import EditISFPDataPopup from '@/shared/components/EditISFPDataPopup'
import HamburgerMenu from '@/shared/components/HamburgerMenu'
import HiddenMeasuresDialog from '@/shared/components/HiddenMeasuresDialog'
import LoadingIndicator from '@/shared/components/LoadingIndicator'
import MeasureDetailsPopup from '@/shared/components/MeasureDetailsPopup'
import { useAssessment, useCustomPackages } from '@/shared/hooks'
import type { FirestoreCustomPackageDocument, RetrofitName, RetrofitState } from '@/shared/types'

import { useCreateCustomPackage, useDeleteCustomPackage, useSetCustomPackageAsMain } from '../api'
import CustomPackages from '../components/CustomPackages'
import MeasuresTable from '../components/MeasuresTable'
import SelectedMeasures from '../components/SelectedMeasures'
import TabToggle from '../components/TabToggle'
import { MeasuresTab } from '../types'

const ArrowSvg = () => (
  <svg width="44" height="81" viewBox="0 0 44 81" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      opacity="0.56"
      d="M7.2188 10.6015C9.64571 37.2193 21.6782 60.1956 42.8401 80.9822C43.7999 78.6745 42.3045 77.4693 41.3503 76.4857C34.3473 68.8091 29.3524 60.1765 24.314 51.6578C17.0318 39.3877 13.6931 26.6247 12.3696 13.5014C12.2731 12.9349 12.4013 12.3945 12.5659 11.1445C16.984 16.5768 18.0046 23.1207 23.2614 27.3814C23.5732 27.1797 24.0202 27.0334 24.332 26.8317C22.5352 22.9341 20.6925 18.9518 18.9851 15.0249C17.2777 11.098 15.7056 7.22649 13.9088 3.32885C12.5733 0.476416 10.5003 -0.240787 8.36864 1.65285C5.57 4.06378 3.31494 6.89492 0.878679 9.58599C0.61282 9.87234 0.303431 10.2726 0.308169 10.6698C0.455316 11.7181 0.87068 12.6787 1.15313 13.7824C2.139 13.6592 3.35196 13.7607 4.11076 13.4127C5.09189 12.8923 5.66479 12.0071 7.2188 10.6015Z"
      fill="#74777F"
    />
  </svg>
)

const MeasuresOverviewPage = () => {
  const { buildingUuid } = useParams<{ buildingUuid: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Initialize tab from URL or default to Overview
  const tabFromUrl = searchParams.get('tab')?.toUpperCase()
  const initialTab =
    tabFromUrl === MeasuresTab.Packages ? MeasuresTab.Packages : MeasuresTab.Overview

  const [selectedTab, setSelectedTab] = useState<MeasuresTab>(initialTab)
  const [selectedMeasureIds, setSelectedMeasureIds] = useState<RetrofitName[]>([])
  const [customPackages, setCustomPackages] = useState<FirestoreCustomPackageDocument[]>([])
  const [selectedMeasure, setSelectedMeasure] = useState<string | null>(null)
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [hasOpenedPopup, setHasOpenedPopup] = useState(false)
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false)
  const [isHiddenMeasuresDialogOpen, setIsHiddenMeasuresDialogOpen] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [hiddenRetrofits, setHiddenRetrofits] = useState<RetrofitName[] | undefined>(undefined)

  // Update URL when tab changes
  const handleTabChange = (newTab: MeasuresTab) => setSearchParams({ tab: newTab.toLowerCase() })
  useEffect(() => setSelectedTab(initialTab), [initialTab])

  const handleMeasureClick = (measure: RetrofitState) => {
    setSelectedMeasure(measure.retrofitName)
    setIsPopupOpen(true)
    setHasOpenedPopup(true)
  }

  const handleClosePopup = () => {
    setIsPopupOpen(false)
  }

  const handleAddToSelection = (retrofitName: RetrofitName) => {
    setSelectedMeasureIds(prev => {
      if (prev.includes(retrofitName)) {
        return prev
      }
      return [...prev, retrofitName]
    })
  }

  const { assessment } = useAssessment(buildingUuid)
  const {
    data: { demandBasedAssessment, consumptionBasedAssessment },
    hiddenRetrofits: assessmentHiddenRetrofits,
    mainPackageId,
    status,
  } = assessment || { data: {} }

  // Sync hiddenRetrofits state with assessment data
  useEffect(() => setHiddenRetrofits(assessmentHiddenRetrofits), [assessmentHiddenRetrofits])

  // Fetch custom packages
  const { customPackages: packages } = useCustomPackages(buildingUuid)
  useEffect(() => {
    if (mainPackageId) {
      // Put selected package first
      const selectedPackageIndex = packages.findIndex(p => p.id === mainPackageId)
      const sortedPackages = [...packages]
      if (selectedPackageIndex !== -1) {
        const [selectedPackage] = sortedPackages.splice(selectedPackageIndex, 1)
        sortedPackages.unshift(selectedPackage)
      }
      setCustomPackages(sortedPackages)
    } else {
      setCustomPackages(packages)
    }
  }, [packages, mainPackageId])

  // Create custom package mutation
  const { mutate: createCustomPackage, isPending: isCreatingPackage } = useCreateCustomPackage({
    onSuccess: () => {
      // Clear selection
      setSelectedMeasureIds([])
      // Switch to packages tab (custom packages will update automatically via Firestore listener)
      handleTabChange(MeasuresTab.Packages)
    },
    onError: error => {
      toast.error(`Fehler beim Erstellen des Pakets: ${error.message || error}`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
      })
    },
  })

  const handleCreatePackage = () => {
    if (selectedMeasureIds.length === 0) {
      return
    }

    createCustomPackage({
      buildingUuid: buildingUuid!,
      retrofitNames: selectedMeasureIds,
    })
  }

  const handleNavigateToCurrentRating = () => {
    navigate(`/${buildingUuid}/current-rating`)
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
  }

  const handleCustomizeOverview = () => {
    setIsHiddenMeasuresDialogOpen(true)
    handleMenuClose()
  }

  const handleHiddenMeasureChange = (hiddenMeasures: RetrofitName[]) => {
    // Update local state
    setHiddenRetrofits(hiddenMeasures)
  }

  // Set custom package as main mutation
  const { mutate: setCustomPackageAsMain, isPending: isSettingPackage } = useSetCustomPackageAsMain(
    {
      onSuccess: () => {
        toast.success('Paket erfolgreich ausgewählt', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
        })
        navigate(`/${buildingUuid}/current-rating`)
      },
      onError: error => {
        toast.error(`Fehler beim Auswählen des Pakets: ${error.message || error}`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
        })
      },
    }
  )

  const handleSelectPackage = (packageId: string) => {
    // If the package is already selected, just navigate to current-rating page
    if (packageId === mainPackageId) {
      navigate(`/${buildingUuid}/current-rating`)
      return
    }

    setCustomPackageAsMain({
      customPackageUuid: packageId,
    })
  }

  // Delete custom package mutation
  const { mutate: deleteCustomPackage, isPending: isDeletingPackage } = useDeleteCustomPackage({
    onSuccess: () => {
      toast.success('Paket erfolgreich gelöscht', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
      })
    },
    onError: error => {
      toast.error(`Fehler beim Löschen des Pakets: ${error.message || error}`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
      })
    },
  })

  const handleDeletePackage = (packageId: string) => {
    deleteCustomPackage({
      customPackageUuid: packageId,
    })
  }

  // Fetch retrofit financing data only when popup has been opened for the first time
  const { data: financing } = useRetrofitFinancing(buildingUuid, {
    enabled: hasOpenedPopup,
  })

  // Get measures and current energy label from assessment
  const measuresD = status === 'failure' ? [] : demandBasedAssessment?.retrofitStates || []
  const measuresC = status === 'failure' ? [] : consumptionBasedAssessment?.retrofitStates || []

  // Get visible measures only
  const visibleMeasures = measuresC.filter(m => !hiddenRetrofits?.includes(m.retrofitName))

  // If selected measures are hidden, remove them from selection
  useEffect(() => {
    setSelectedMeasureIds(prev => prev.filter(measureId => !hiddenRetrofits?.includes(measureId)))
  }, [hiddenRetrofits])

  // Get selected measures from IDs
  const selectedMeasures = visibleMeasures.filter(m => selectedMeasureIds.includes(m.retrofitName))

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        minHeight: '100vh',
        width: '100vw',
        backgroundColor: '#ffffff',
        padding: { xs: '0 24px' },
      }}
    >
      <HamburgerMenu
        sx={{
          position: 'fixed',
          top: 32,
          left: 32,
          display: { xs: 'flex', lg: 'none' },
        }}
      />

      <Box
        sx={{
          maxWidth: { xs: '100%', lg: '1125px' },
          width: '100%',
          paddingBottom:
            selectedTab === MeasuresTab.Overview
              ? { xs: selectedMeasureIds.length > 0 ? '230px' : '180px', lg: '60px' }
              : 14,
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            background:
              'linear-gradient(180deg, white 0px, white calc(100% - 12px), transparent 100%)',
            zIndex: 100,
            paddingTop: { xs: 10, lg: 5 },
            paddingBottom: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 3 }}>
            <Box sx={{ display: { xs: 'none', lg: 'flex' } }}>
              <HamburgerMenu />
            </Box>
            <Typography
              component="h1"
              sx={{
                fontWeight: 600,
                fontSize: '28px',
                lineHeight: '42px',
                marginLeft: '8px',
              }}
            >
              <Box component="span" sx={{ color: '#5BC06B' }}>
                Maßnahmen
              </Box>{' '}
              <Box component="span">im Überblick</Box>
            </Typography>
          </Box>

          <TabToggle selectedTab={selectedTab} onTabChange={handleTabChange} />

          {selectedTab === MeasuresTab.Overview && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginX: '4px',
              }}
            >
              <BedarfVerbrauchLabel type={BedarfVerbrauchType.VERBRAUCH} />
              <IconButton
                onClick={handleMenuOpen}
                sx={{
                  padding: '12px',
                  margin: '-12px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <DotsVerticalIcon style={{ width: 24, height: 24, color: '#181D29' }} />
              </IconButton>
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                slotProps={{
                  paper: {
                    sx: {
                      padding: '8px',
                      borderRadius: '24px',
                    },
                  },
                }}
              >
                <MenuItem
                  onClick={handleCustomizeOverview}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '16px',
                    gap: '12px',
                    padding: '8px',
                    marginY: '-8px',
                    fontWeight: 500,
                    fontSize: '16px',
                    color: '#111111',
                  }}
                >
                  <Box
                    sx={{
                      padding: '8px',
                      border: '1px solid #F0F0F0',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FilterLinesIcon style={{ width: 24, height: 24, color: '#181D29' }} />
                  </Box>
                  Überblick anpassen
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Box>

        {selectedTab === MeasuresTab.Overview && (
          <>
            <Box
              sx={{
                marginTop: 1,
                display: { xs: 'block', lg: 'flex' },
                gap: { lg: '24px' },
              }}
            >
              {/* Main content area - measures list */}
              <Box sx={{ flex: { lg: '0 0 744px' }, width: { xs: '100%', lg: '744px' } }}>
                {assessment && (
                  <MeasuresTable
                    measures={visibleMeasures}
                    selectedMeasureIds={selectedMeasureIds}
                    onSelectionChange={setSelectedMeasureIds}
                    onMeasureClick={handleMeasureClick}
                  />
                )}
                {!assessment && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
                    <LoadingIndicator size={150} />
                  </Box>
                )}
                {status === 'failure' && (
                  <Alert
                    severity="error"
                    icon={<AlertCircleIcon style={{ width: 20, height: 20 }} />}
                    onClick={() => setIsEditPopupOpen(true)}
                    sx={{
                      cursor: 'pointer',
                      marginTop: 2,
                      '&:hover': {
                        backgroundColor: '#FDEDED',
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                      Die Berechnung ist fehlgeschlagen. Bitte überprüfen und korrigieren Sie Ihre
                      Eingaben.
                    </Typography>
                  </Alert>
                )}
              </Box>

              {/* SelectedMeasures sidebar - desktop only inside this tab */}
              <Box
                sx={{
                  display: { xs: 'none', lg: 'block' },
                  position: 'sticky',
                  top: '214px',
                  flex: 1,
                  alignSelf: 'flex-start',
                }}
              >
                <SelectedMeasures
                  selectedMeasures={selectedMeasures}
                  onCreatePackage={handleCreatePackage}
                  isLoading={isCreatingPackage}
                />
              </Box>
            </Box>

            {/* SelectedMeasures bottom panel - mobile only */}
            <Box
              sx={{
                display: { xs: 'block', lg: 'none' },
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                marginX: '8px',
                zIndex: 10,
              }}
            >
              <SelectedMeasures
                selectedMeasures={selectedMeasures}
                onCreatePackage={handleCreatePackage}
                isLoading={isCreatingPackage}
              />
            </Box>
          </>
        )}

        {selectedTab === MeasuresTab.Packages && (
          <>
            {customPackages.length > 0 ? (
              <CustomPackages
                packages={customPackages}
                selectedPackageId={mainPackageId}
                assessment={assessment?.data}
                onSelectPackage={handleSelectPackage}
                isSelectingPackage={isSettingPackage}
                onDeletePackage={handleDeletePackage}
                isDeletingPackage={isDeletingPackage}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  marginTop: 7,
                  marginLeft: '23%',
                }}
              >
                <ArrowSvg />
                <Typography
                  sx={{
                    marginTop: 4,
                    marginLeft: 8,
                    fontSize: '12px',
                    lineHeight: '18px',
                    color: '#464A54',
                  }}
                >
                  Erstellen Sie ein Paket in der Übersicht
                </Typography>
              </Box>
            )}

            <Button
              endIcon={<ArrowNarrowRightIcon />}
              onClick={handleNavigateToCurrentRating}
              disabled={!packages.map(p => p.id).includes(mainPackageId || '')}
              sx={{
                position: 'fixed',
                bottom: { xs: 24, lg: 32 },
                right: { xs: 24, lg: 32 },
                zIndex: 2,
              }}
            >
              Bewertung ansehen
            </Button>
          </>
        )}
      </Box>

      {/* Measure Details Popup */}
      {assessment?.data && (
        <MeasureDetailsPopup
          open={isPopupOpen}
          onClose={handleClosePopup}
          measureC={measuresC.find(r => r.retrofitName === selectedMeasure) || null}
          measureD={measuresD.find(r => r.retrofitName === selectedMeasure) || null}
          assessment={assessment.data}
          financing={financing?.find(f => f.retrofitName === selectedMeasure)}
          onAddToSelection={handleAddToSelection}
        />
      )}

      <EditISFPDataPopup open={isEditPopupOpen} onClose={() => setIsEditPopupOpen(false)} />

      <HiddenMeasuresDialog
        open={isHiddenMeasuresDialogOpen}
        onClose={() => setIsHiddenMeasuresDialogOpen(false)}
        availableMeasures={measuresC.map(m => m.retrofitName)}
        currentHiddenMeasures={hiddenRetrofits || []}
        buildingUuid={buildingUuid!}
        onHiddenMeasureChange={handleHiddenMeasureChange}
      />
    </Box>
  )
}

export default MeasuresOverviewPage
